import React, { useId, useMemo } from 'react';
import { motion, MotionConfig, useReducedMotion, type Transition } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
  abstractBlurRange,
  abstractOpacityRange,
  memorySpeciesGrammars,
  motionDurations,
  motionEasings,
  sharpenOnHover,
} from '@/lib/motion-tokens';

const BRAND = { purple: '#7C3AED', indigo: '#4F46E5', sky: '#38BDF8' };

export type SceneVariant = 'card' | 'cinematic';

export interface SceneProps {
  className?: string;
  compact?: boolean;
  isHovered?: boolean;
  variant?: SceneVariant;
}

function sceneHeights(compact: boolean, variant: SceneVariant) {
  if (variant === 'cinematic') {
    return compact ? 'h-56' : 'h-88';
  }
  return compact ? 'h-28' : 'h-44';
}

function SceneCanvas({
  compact,
  variant,
  className,
  children,
}: {
  compact: boolean;
  variant: SceneVariant;
  className: string;
  children: React.ReactNode;
}) {
  const height = sceneHeights(compact, variant);
  return (
    <div className={cn('relative w-full', height, className)} aria-hidden>
      <div className={cn('w-full', height)} />
      <div className="absolute inset-0">{children}</div>
    </div>
  );
}

function DotGrid({ patternId }: { patternId: string }) {
  return (
    <svg className="absolute inset-0 h-full w-full opacity-30" aria-hidden>
      <defs>
        <pattern id={patternId} width="12" height="12" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1" fill="#a78bfa" fillOpacity="0.35" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
}

function LightFieldVignette() {
  return (
    <div
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(255,255,255,0.55)_100%)]"
      aria-hidden
    />
  );
}

function GrainOverlay() {
  return (
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.09] mix-blend-multiply"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }}
      aria-hidden
    />
  );
}

function speciesLoopTransition(
  duration: number,
  prefersReducedMotion: boolean | null,
  ease: Transition['ease'] = motionEasings.calmLoop,
): Transition {
  if (prefersReducedMotion) {
    return { duration: 0 };
  }
  return {
    duration,
    repeat: Infinity,
    repeatType: 'loop',
    ease,
  };
}

type PlaneConfig = {
  scatter: { x: number; y: number; rotate: number };
  aligned: { x: number; y: number; rotate: number };
  gradient: string;
  size: { w: string; h: string };
};

function TranslucentPlane({
  config,
  index,
  isHovered,
  prefersReducedMotion,
}: {
  config: PlaneConfig;
  index: number;
  isHovered?: boolean;
  prefersReducedMotion: boolean | null;
  compact?: boolean;
}) {
  const sharpen = sharpenOnHover(isHovered);
  const grammar = memorySpeciesGrammars.stillBloom;
  const totalDuration =
    grammar.fragment * sharpen.driftDurationFactor + index * 0.08;
  const alignDuration = grammar.align * sharpen.alignDurationFactor;
  const holdDuration = grammar.hold * sharpen.holdDurationFactor;
  const alignEnd = alignDuration / totalDuration;
  const holdEnd = (alignDuration + holdDuration) / totalDuration;

  const { scatter, aligned } = config;
  const blur = sharpen.planeBlur;

  if (prefersReducedMotion) {
    return (
      <div
        className={cn(
          'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md border border-white/50 bg-gradient-to-br shadow-sm backdrop-blur-sm',
          config.size.w,
          config.size.h,
          config.gradient,
        )}
        style={{
          transform: `translate(calc(-50% + ${aligned.x}px), calc(-50% + ${aligned.y}px)) rotate(${aligned.rotate}deg)`,
          filter: `blur(${abstractBlurRange.mid})`,
          opacity: abstractOpacityRange.mid + 0.15,
        }}
      />
    );
  }

  return (
    <motion.div
      className={cn(
        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md border border-white/50 bg-gradient-to-br shadow-sm',
        config.size.w,
        config.size.h,
        config.gradient,
      )}
      style={{ filter: `blur(${blur})` }}
      animate={{
        x: [scatter.x, aligned.x, aligned.x, scatter.x],
        y: [scatter.y, aligned.y, aligned.y, scatter.y],
        rotate: [scatter.rotate, aligned.rotate, aligned.rotate, scatter.rotate],
        opacity: [0.35, 0.72, 0.72, 0.35],
      }}
      transition={{
        duration: totalDuration,
        times: [0, alignEnd, holdEnd, 1],
        repeat: Infinity,
        ease: motionEasings.calmLoop,
        delay: index * 0.12,
      }}
    />
  );
}

function EchoRing({
  cx,
  cy,
  delay,
  isHovered,
  prefersReducedMotion,
  compact,
}: {
  cx: number;
  cy: number;
  delay: number;
  isHovered?: boolean;
  prefersReducedMotion: boolean | null;
  compact: boolean;
}) {
  const sharpen = sharpenOnHover(isHovered);
  const decay = memorySpeciesGrammars.echoThread.decay * sharpen.echoDecayFactor;
  const maxR = compact ? 22 : 28;

  if (prefersReducedMotion) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={maxR * 0.6}
        fill="none"
        stroke={BRAND.sky}
        strokeWidth="1"
        opacity={abstractOpacityRange.mid}
      />
    );
  }

  return (
    <motion.circle
      cx={cx}
      cy={cy}
      fill="none"
      stroke={BRAND.sky}
      strokeWidth="1.5"
      initial={{ r: 6, opacity: 0 }}
      animate={{ r: [6, maxR], opacity: [0.55, 0] }}
      transition={{
        duration: decay,
        delay,
        repeat: Infinity,
        repeatDelay: memorySpeciesGrammars.echoThread.pulse * 2 * sharpen.pulseDurationFactor,
        ease: motionEasings.easeOut,
      }}
    />
  );
}

function FrameShard({
  top,
  width,
  height,
  driftDuration,
  delay,
  skip,
  isHovered,
  prefersReducedMotion,
  compact,
}: {
  top: number;
  width: number;
  height: number;
  driftDuration: number;
  delay: number;
  skip?: boolean;
  isHovered?: boolean;
  prefersReducedMotion: boolean | null;
  compact: boolean;
}) {
  const sharpen = sharpenOnHover(isHovered);
  const duration = driftDuration * sharpen.driftDurationFactor;
  const suppressSkip = sharpen.suppressSkip;
  const driftDistance = compact ? -80 : -110;

  if (prefersReducedMotion) {
    return (
      <div
        className="absolute rounded-sm border border-purple-300/40 bg-gradient-to-br from-purple-400/35 to-indigo-400/25"
        style={{
          top,
          left: '35%',
          width,
          height,
          opacity: abstractOpacityRange.mid + 0.1,
        }}
      />
    );
  }

  const opacityKeyframes = skip && !suppressSkip
    ? [0.5, 0.55, 0.2, 0.55, 0.5]
    : [0.45, 0.6, 0.55, 0.6, 0.45];

  return (
    <motion.div
      className="absolute rounded-sm border border-purple-300/40 bg-gradient-to-br from-purple-400/40 to-indigo-400/30"
      style={{ top, width, height, left: '10%' }}
      animate={{
        x: [0, driftDistance * 0.5, driftDistance],
        opacity: opacityKeyframes,
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: motionEasings.linear,
        times: skip && !suppressSkip ? [0, 0.45, 0.5, 0.55, 1] : [0, 0.25, 0.5, 0.75, 1],
      }}
    />
  );
}

function getStillBloomPlanes(variant: SceneVariant, compact: boolean): PlaneConfig[] {
  const small = compact || variant === 'card';
  const base: PlaneConfig[] = [
    {
      scatter: { x: -38, y: -18, rotate: -12 },
      aligned: { x: -12, y: -4, rotate: -4 },
      gradient: 'from-orange-300/55 to-rose-200/45',
      size: { w: small ? 'w-14' : 'w-20', h: small ? 'h-10' : 'h-14' },
    },
    {
      scatter: { x: 32, y: -22, rotate: 10 },
      aligned: { x: 8, y: -2, rotate: 3 },
      gradient: 'from-amber-200/50 to-orange-300/40',
      size: { w: small ? 'w-12' : 'w-16', h: small ? 'h-9' : 'h-12' },
    },
    {
      scatter: { x: -8, y: 24, rotate: -6 },
      aligned: { x: 0, y: 6, rotate: 0 },
      gradient: 'from-rose-200/45 to-purple-300/35',
      size: { w: small ? 'w-16' : 'w-24', h: small ? 'h-11' : 'h-16' },
    },
  ];

  if (variant === 'cinematic') {
    return [
      ...base,
      {
        scatter: { x: 48, y: 16, rotate: 14 },
        aligned: { x: 16, y: 4, rotate: 5 },
        gradient: 'from-purple-200/40 to-indigo-300/35',
        size: { w: small ? 'w-14' : 'w-20', h: small ? 'h-10' : 'h-14' },
      },
      {
        scatter: { x: -52, y: 8, rotate: -14 },
        aligned: { x: -18, y: 2, rotate: -6 },
        gradient: 'from-orange-200/40 to-amber-300/35',
        size: { w: small ? 'w-12' : 'w-16', h: small ? 'h-8' : 'h-12' },
      },
      {
        scatter: { x: 12, y: -32, rotate: 6 },
        aligned: { x: 4, y: -8, rotate: 2 },
        gradient: 'from-rose-300/35 to-purple-200/30',
        size: { w: small ? 'w-10' : 'w-14', h: small ? 'h-7' : 'h-10' },
      },
    ];
  }

  return base;
}

export function VisualMemoryScene({
  className = '',
  compact = false,
  isHovered,
  variant = 'card',
}: SceneProps) {
  const patternId = useId().replace(/:/g, '');
  const prefersReducedMotion = useReducedMotion();
  const sharpen = sharpenOnHover(isHovered);
  const planes = useMemo(() => getStillBloomPlanes(variant, compact), [variant, compact]);
  const cycleDuration =
    memorySpeciesGrammars.stillBloom.fragment * sharpen.driftDurationFactor;

  return (
    <SceneCanvas
      compact={compact}
      variant={variant}
      className={cn(
        'overflow-hidden rounded-xl border border-orange-200/80 bg-gradient-to-br from-amber-50 via-orange-50/90 to-purple-50 shadow-sm',
        className,
      )}
    >
      <DotGrid patternId={patternId} />
      <LightFieldVignette />
      <GrainOverlay />

      <div className="absolute inset-0 flex items-center justify-center">
        {planes.map((plane, i) => (
          <TranslucentPlane
            key={i}
            config={plane}
            index={i}
            isHovered={isHovered}
            prefersReducedMotion={prefersReducedMotion}
          />
        ))}
      </div>

      {variant === 'cinematic' && (
        <motion.div
          className={cn(
            'absolute rounded-full bg-gradient-to-r from-orange-400/30 to-transparent',
            compact ? 'left-1/4 top-1/3 h-8 w-24 blur-md' : 'left-1/4 top-1/3 h-12 w-32 blur-lg',
          )}
          animate={
            prefersReducedMotion
              ? { opacity: abstractOpacityRange.mid }
              : { opacity: [0.2, 0.45, 0.2], scaleX: [0.8, 1.1, 0.8] }
          }
          transition={speciesLoopTransition(cycleDuration, prefersReducedMotion)}
        />
      )}

      <motion.div
        className={cn(
          'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-300/60 blur-md',
          compact ? 'h-2 w-2' : 'h-2.5 w-2.5',
        )}
        animate={
          prefersReducedMotion
            ? { opacity: abstractOpacityRange.mid, scale: 1 }
            : { opacity: [0.3, 0.7, 0.3], scale: [0.8, 2.2, 0.8] }
        }
        transition={speciesLoopTransition(
          motionDurations.loopCinematic * sharpen.driftDurationFactor,
          prefersReducedMotion,
        )}
      />
    </SceneCanvas>
  );
}

export function VoiceMemoryScene({
  className = '',
  compact = false,
  isHovered,
  variant = 'card',
}: SceneProps) {
  const patternId = useId().replace(/:/g, '');
  const gradientId = useId().replace(/:/g, '');
  const prefersReducedMotion = useReducedMotion();
  const sharpen = sharpenOnHover(isHovered);
  const grammar = memorySpeciesGrammars.echoThread;
  const pulseCycle = grammar.pulse * 3 * sharpen.pulseDurationFactor;
  const threadY = compact ? 56 : 75;
  const echoPoints =
    variant === 'cinematic'
      ? compact
        ? [60, 100, 140, 180, 220]
        : [50, 95, 140, 185, 230, 265]
      : compact
        ? [70, 130, 190]
        : [60, 150, 240];

  return (
    <SceneCanvas
      compact={compact}
      variant={variant}
      className={cn(
        'overflow-hidden rounded-xl border border-blue-200/80 bg-gradient-to-br from-sky-50 via-blue-50/90 to-indigo-50 shadow-sm',
        className,
      )}
    >
      <DotGrid patternId={patternId} />
      <LightFieldVignette />
      <GrainOverlay />

      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 300 150">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={BRAND.indigo} stopOpacity="0.3" />
            <stop offset="50%" stopColor={BRAND.purple} stopOpacity="0.9" />
            <stop offset="100%" stopColor={BRAND.sky} stopOpacity="0.4" />
          </linearGradient>
        </defs>

        <motion.path
          d={`M 20 ${threadY} Q 150 ${threadY - 12} 280 ${threadY}`}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={variant === 'cinematic' ? 2.5 : 2}
          strokeLinecap="round"
          strokeDasharray="8 12"
          animate={
            prefersReducedMotion
              ? { strokeDashoffset: 0, opacity: abstractOpacityRange.mid + 0.2 }
              : { strokeDashoffset: [0, -40], opacity: [0.7, 1, 0.7] }
          }
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : {
                  strokeDashoffset: {
                    duration: pulseCycle,
                    repeat: Infinity,
                    ease: motionEasings.linear,
                  },
                  opacity: {
                    duration: pulseCycle,
                    repeat: Infinity,
                    ease: motionEasings.calmLoop,
                  },
                }
          }
        />

        {variant === 'cinematic' && (
          <motion.path
            d={`M 20 ${threadY + 14} Q 150 ${threadY + 2} 280 ${threadY + 14}`}
            fill="none"
            stroke={BRAND.indigo}
            strokeWidth="1"
            strokeLinecap="round"
            strokeDasharray="4 16"
            opacity={0.35}
            animate={
              prefersReducedMotion
                ? { strokeDashoffset: 0 }
                : { strokeDashoffset: [0, -30] }
            }
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : {
                    duration: pulseCycle * 1.15,
                    repeat: Infinity,
                    ease: motionEasings.linear,
                    delay: 0.3,
                  }
            }
          />
        )}

        {echoPoints.map((cx, i) => (
          <EchoRing
            key={cx}
            cx={cx}
            cy={threadY}
            delay={i * grammar.pulse * sharpen.pulseDurationFactor}
            isHovered={isHovered}
            prefersReducedMotion={prefersReducedMotion}
            compact={compact}
          />
        ))}
      </svg>

      {variant === 'cinematic' &&
        [0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute h-1 w-1 rounded-full bg-indigo-400/60"
            style={{ left: `${30 + i * 25}%`, top: `${compact ? 28 : 32}%` }}
            animate={
              prefersReducedMotion
                ? { opacity: abstractOpacityRange.mid }
                : { opacity: [0.2, 0.7, 0.2] }
            }
            transition={{
              duration: grammar.pulse,
              delay: i * 0.2,
              repeat: Infinity,
              ease: motionEasings.calmLoop,
            }}
          />
        ))}
    </SceneCanvas>
  );
}

export function VideoMemoryScene({
  className = '',
  compact = false,
  isHovered,
  variant = 'card',
}: SceneProps) {
  const patternId = useId().replace(/:/g, '');
  const prefersReducedMotion = useReducedMotion();
  const sharpen = sharpenOnHover(isHovered);
  const grammar = memorySpeciesGrammars.timeCurrent;
  const driftBase = grammar.drift * sharpen.driftDurationFactor;

  const shards =
    variant === 'cinematic'
      ? [
          { top: compact ? 14 : 18, w: compact ? 28 : 36, h: compact ? 18 : 24, drift: driftBase * 1.1, delay: 0 },
          { top: compact ? 28 : 36, w: compact ? 32 : 42, h: compact ? 20 : 28, drift: driftBase * 0.85, delay: 0.4, skip: true },
          { top: compact ? 42 : 54, w: compact ? 24 : 32, h: compact ? 16 : 22, drift: driftBase * 1.25, delay: 0.8 },
          { top: compact ? 20 : 26, w: compact ? 22 : 30, h: compact ? 14 : 20, drift: driftBase * 0.95, delay: 1.2 },
          { top: compact ? 36 : 48, w: compact ? 26 : 34, h: compact ? 17 : 24, drift: driftBase * 1.05, delay: 0.2, rewind: true },
        ]
      : [
          { top: compact ? 18 : 22, w: compact ? 30 : 38, h: compact ? 18 : 24, drift: driftBase, delay: 0, skip: true },
          { top: compact ? 32 : 42, w: compact ? 26 : 34, h: compact ? 16 : 22, drift: driftBase * 1.2, delay: 0.5 },
          { top: compact ? 10 : 14, w: compact ? 24 : 30, h: compact ? 14 : 20, drift: driftBase * 0.9, delay: 1 },
        ];

  return (
    <SceneCanvas
      compact={compact}
      variant={variant}
      className={cn(
        'overflow-hidden rounded-xl border border-purple-200/80 bg-gradient-to-br from-violet-50 via-purple-50/90 to-indigo-50 shadow-sm',
        className,
      )}
    >
      <DotGrid patternId={patternId} />
      <LightFieldVignette />
      <GrainOverlay />

      <motion.div
        className={cn(
          'absolute rounded-sm bg-gradient-to-r from-purple-400/25 to-transparent blur-sm',
          compact ? 'left-4 top-6 h-10 w-16' : 'left-6 top-8 h-14 w-24',
        )}
        animate={
          prefersReducedMotion
            ? { opacity: abstractOpacityRange.mid, x: 20 }
            : { opacity: [0.15, 0.35, 0.15], x: [0, compact ? 40 : 60, compact ? 80 : 110] }
        }
        transition={speciesLoopTransition(driftBase, prefersReducedMotion, motionEasings.linear)}
      />

      {shards.map((shard, i) => {
        if ('rewind' in shard && shard.rewind && variant === 'cinematic') {
          return (
            <motion.div
              key={i}
              className="absolute rounded-sm border border-purple-300/35 bg-gradient-to-br from-purple-400/35 to-indigo-400/25"
              style={{
                top: shard.top,
                width: shard.w,
                height: shard.h,
                left: '20%',
              }}
              animate={
                prefersReducedMotion
                  ? { x: 30, opacity: abstractOpacityRange.mid }
                  : { x: [0, 50, 20, -15, 0], opacity: [0.4, 0.55, 0.5, 0.45, 0.4] }
              }
              transition={{
                duration: driftBase,
                delay: shard.delay,
                repeat: Infinity,
                ease: motionEasings.calmLoop,
              }}
            />
          );
        }

        return (
          <FrameShard
            key={i}
            top={shard.top}
            width={shard.w}
            height={shard.h}
            driftDuration={shard.drift}
            delay={shard.delay}
            skip={'skip' in shard ? shard.skip : false}
            isHovered={isHovered}
            prefersReducedMotion={prefersReducedMotion}
            compact={compact}
          />
        );
      })}
    </SceneCanvas>
  );
}

const SCENE_MAP: Record<string, React.FC<SceneProps>> = {
  'visual-memory': VisualMemoryScene,
  'audio-memory': VoiceMemoryScene,
  'video-memory': VideoMemoryScene,
};

export function MultimodalScene({
  featureId,
  className,
  compact,
  isHovered,
  variant = 'card',
}: {
  featureId: string;
  className?: string;
  compact?: boolean;
  isHovered?: boolean;
  variant?: SceneVariant;
}) {
  const Scene = SCENE_MAP[featureId];
  if (!Scene) return null;

  return (
    <MotionConfig reducedMotion="user">
      <Scene className={className} compact={compact} isHovered={isHovered} variant={variant} />
    </MotionConfig>
  );
}
